import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Upload, Button, Spin } from 'antd';
import { getPreSignedUrlForImage, setValueAtPath, uploadImage } from '../../../redux/actions';
import { SelectReportState } from '../Selector';
import SVGIcons from '../../../Components/SVGIcons';
import { ButtonElement } from '../../../FormElements/ButtonElement';
import { submitToUpload } from '../helper';
import { toast } from '../../../utils/messageUtils';
import { IMAGE_UPLOAD_ERROR } from '../constants';

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

function EditableImage(props) {
  const {
    image,
    width,
    height,
    alt = '',
    fallback = null,
    imagePath,
    widthPath,
    heightPath,
    defaultWidth = 200,
    defaultHeight = 80,
    minWidth = 60,
    maxWidth = 400,
    minHeight = 30,
    maxHeight = 400,
    editable,
    setValueAtPath,
  } = props;
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [temporarySignedUrl, setTemporarySignedUrl] = useState(null)

  const savedW = clamp(Number(width) || defaultWidth, minWidth, maxWidth);
  const savedH = clamp(Number(height) || defaultHeight, minHeight, maxHeight);
  const w = draft ? draft.w : savedW;
  const h = draft ? draft.h : savedH;

  const afterUpload = (isSuccess, imageData) => {
    if (isSuccess) {
      setValueAtPath(imagePath, imageData.fileurl);
      if (width == null) setValueAtPath(widthPath, defaultWidth);
      if (height == null) setValueAtPath(heightPath, defaultHeight);
      setTemporarySignedUrl(imageData.signedUrl)
      setLoading(false);
    } else {
      toast.error(IMAGE_UPLOAD_ERROR);
      setLoading(false);
    }
  };

  const handleRemove = () => {
    setValueAtPath(imagePath, null);
  };

  // axis: 'x' = width only, 'y' = height only, 'xy' = both (corner handle)
  const startResize = (axis) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = savedW;
    const startH = savedH;
    let curW = startW;
    let curH = startH;

    const onMove = (ev) => {
      if (axis !== 'y') {
        curW = clamp(startW + (ev.clientX - startX), minWidth, maxWidth);
      }
      if (axis !== 'x') {
        curH = clamp(startH + (ev.clientY - startY), minHeight, maxHeight);
      }
      setDraft({ w: curW, h: curH });
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (curW !== startW) setValueAtPath(widthPath, curW);
      if (curH !== startH) setValueAtPath(heightPath, curH);
      setDraft(null);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  if (!editable) {
    if (image) {
      return (
        <img
          src={image}
          alt={alt}
          className="cover-page__org-image"
          style={{ width: `${savedW}px`, height: `${savedH}px` }}
        />
      );
    }
    return fallback;
  }

  const handleBeforeUpload = (file) => {
    setLoading(true);
    submitToUpload({
      props: props,
      fileList: [{ originFileObj: file, name: file.name, type: file.type }],
      afterUpload: afterUpload,
    });
    return Upload.LIST_IGNORE;
  };

  return (
    <Spin spinning={loading} tip='loading image...'>
      <div className="cover-page__org-name-editor">
        {image ? (
          <div
            className="cover-page__org-image-frame"
            style={{ width: `${w}px`, height: `${h}px` }}
          >
            <img
              src={temporarySignedUrl ? temporarySignedUrl : image}
              alt={alt}
              className="cover-page__org-image"
              draggable={false}
            />
            <span
              className="cover-page__org-image-handle cover-page__org-image-handle--e"
              onMouseDown={startResize('x')}
              title="Drag to resize width"
            />
            <span
              className="cover-page__org-image-handle cover-page__org-image-handle--s"
              onMouseDown={startResize('y')}
              title="Drag to resize height"
            />
            <span
              className="cover-page__org-image-handle cover-page__org-image-handle--se"
              onMouseDown={startResize('xy')}
              title="Drag to resize"
            />
            <span className="cover-page__org-image-size">{w} × {h}</span>
          </div>
        ) : (
          fallback
        )}

        {!loading && (
          <div className="cover-page__org-name-editor-controls">
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={handleBeforeUpload}
            >
              <ButtonElement className="upload-report">
                <SVGIcons type={"SVG-upload"} /> {image ? 'Replace image' : 'Upload image'}
              </ButtonElement>
            </Upload>
            {image && (
              <Button size="small" danger onClick={handleRemove}>
                Remove
              </Button>
            )}
          </div>
        )}
      </div>
    </Spin>
  );
}

const mapStateToProps = (state) => ({
  editable: SelectReportState(state).editable,
});

const mapDispatchToProps = (dispatch) => ({
  setValueAtPath: (path, value) => dispatch(setValueAtPath(path, value)),
  uploadImage: (params, next) => dispatch(uploadImage(params, next)),
  getPreSignedUrlForImage: (params, next) => dispatch(getPreSignedUrlForImage(params, next)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditableImage);
